import { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { useOrganization } from "../context/OrganizationContext";
import { UpsellBanner } from "../components/Upsell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import {
  FileText, Download, FileCode, FileType, Calendar, Hash,
  CheckCircle, Clock, XCircle, AlertTriangle, Send
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  generated: { label: "Generado", cls: "bg-sky-500/10 text-sky-400 border-sky-500/20", icon: FileText },
  submitted: { label: "Enviado", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20", icon: Send },
  accepted: { label: "Aceptado", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", icon: CheckCircle },
  rejected: { label: "Rechazado", cls: "bg-red-500/10 text-red-400 border-red-500/20", icon: XCircle },
  corrected: { label: "Corregido", cls: "bg-violet-500/10 text-violet-400 border-violet-500/20", icon: AlertTriangle },
};

export default function AmlReports() {
  const { hasFeature, hasPermission, getRequiredPlan } = useOrganization();
  const [reports, setReports] = useState([]);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [showNodDialog, setShowNodDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(null);

  const hasXmlFeature = hasFeature("reportes_xml_sat_uif");
  const hasXmlPermission = hasPermission("generate_xml_reports");

  useEffect(() => { 
    if (hasXmlFeature && hasXmlPermission) {
      loadData(); 
    } else {
      setLoading(false);
    }
  }, [hasXmlFeature, hasXmlPermission]);

  const loadData = async () => {
    try {
      const [rptRes, opsRes] = await Promise.all([
        axios.get(`${API}/aml-reports`, { withCredentials: true }),
        axios.get(`${API}/operations?limit=50`, { withCredentials: true }),
      ]);
      setReports(rptRes.data.items || []);
      setOperations(opsRes.data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  // Show upsell if feature not available OR if user doesn't have permission
  if (!hasXmlFeature || !hasXmlPermission) {
    return (
      <Layout>
        <div data-testid="aml-reports-page">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white">Avisos AML (XML)</h1>
            <p className="text-slate-400 mt-1">Genera reportes XML para SAT y UIF</p>
          </div>
          <UpsellBanner 
            feature="reportes_xml_sat_uif" 
            requiredPlan={getRequiredPlan("reportes_xml_sat_uif")} 
          />
        </div>
      </Layout>
    );
  }

  const generateAviso = async (operationId) => {
    setGenerating(operationId);
    try {
      const res = await axios.post(`${API}/aml-reports/aviso/${operationId}`, {}, { withCredentials: true });
      toast.success(`Aviso generado: ${res.data.report_id}`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando aviso");
    } finally {
      setGenerating(null);
    }
  };

  const downloadReport = async (reportId, format) => {
    try {
      const res = await axios.get(`${API}/aml-reports/aviso/${reportId}/download?format=${format}`, {
        withCredentials: true, responseType: "blob",
      });
      const contentType = format === "pdf" ? "application/pdf" : format === "xml" ? "application/xml" : "text/plain";
      const ext = format;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Aviso_${reportId}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Descargando ${format.toUpperCase()}`);
    } catch (e) {
      toast.error("Error descargando reporte");
    }
  };

  // Reportable operations: AML-sensitive or exceeds threshold
  const reportableOps = operations.filter(op => op.is_aml_sensitive || op.exceeds_threshold);
  const generatedOpIds = new Set(reports.filter(r => r.report_type === "aviso").map(r => r.operation_id));

  return (
    <Layout>
      <div data-testid="aml-reports-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Avisos AML / Portal PLD</h1>
            <p className="text-slate-400 mt-1 text-sm">Generacion de avisos para el Portal de Prevencion de Lavado de Dinero</p>
          </div>
          <Button onClick={() => setShowNodDialog(true)} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="nod-report-btn">
            <FileText size={16} className="mr-2" /> Generar NOD
          </Button>
        </div>

        {/* Reportable Operations */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl mb-6" data-testid="reportable-operations">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="text-sm font-semibold text-white">Operaciones Reportables</h2>
            <p className="text-xs text-slate-500 mt-1">Actividades vulnerables y operaciones que exceden umbral</p>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : reportableOps.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No hay operaciones reportables</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800 text-left">
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">No.</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Tipo</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Monto</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Riesgo</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Aviso</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Accion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {reportableOps.map((op) => {
                    const hasReport = generatedOpIds.has(op.operation_id);
                    return (
                      <tr key={op.operation_id} className="hover:bg-slate-800/50">
                        <td className="px-5 py-2.5 text-sm text-teal-400 font-mono">{op.operation_number}</td>
                        <td className="px-5 py-2.5 text-sm text-slate-300">{op.operation_type_name || op.operation_type}</td>
                        <td className="px-5 py-2.5 text-sm text-slate-200 font-mono">
                          ${Number(op.amount_mxn).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-5 py-2.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            op.risk_level === "critical" ? "bg-red-500/10 text-red-400" :
                            op.risk_level === "high" ? "bg-orange-500/10 text-orange-400" :
                            op.risk_level === "medium" ? "bg-amber-500/10 text-amber-400" :
                            "bg-emerald-500/10 text-emerald-400"
                          }`}>{(op.risk_level || "low").toUpperCase()}</span>
                        </td>
                        <td className="px-5 py-2.5 text-xs">
                          {hasReport ? (
                            <span className="text-emerald-400">Generado</span>
                          ) : (
                            <span className="text-slate-500">Pendiente</span>
                          )}
                        </td>
                        <td className="px-5 py-2.5">
                          {hasReport ? (
                            <span className="text-xs text-slate-500">Ya generado</span>
                          ) : (
                            <Button size="sm" onClick={() => generateAviso(op.operation_id)}
                              disabled={generating === op.operation_id}
                              className="h-7 text-xs" data-testid={`gen-aviso-${op.operation_id}`}>
                              {generating === op.operation_id ? "..." : "Generar Aviso"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Generated Reports */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl" data-testid="generated-reports">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="text-sm font-semibold text-white">Avisos Generados</h2>
            <p className="text-xs text-slate-500 mt-1">Descarga en XML, TXT o PDF</p>
          </div>
          {reports.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No hay avisos generados</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {reports.map((r) => {
                const sc = statusConfig[r.submission_status] || statusConfig.generated;
                const StatusIcon = sc.icon;
                return (
                  <div key={r.report_id} className="px-5 py-3 flex items-center justify-between" data-testid={`report-row-${r.report_id}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0">
                        <FileText size={14} className="text-teal-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{r.report_id}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-2">
                          <Hash size={10} /> {r.report_type}
                          {r.amount_mxn > 0 && <> &middot; ${Number(r.amount_mxn).toLocaleString("es-MX")}</>}
                          {r.generated_at && <><Calendar size={10} className="ml-1" /> {new Date(r.generated_at).toLocaleDateString("es-MX")}</>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setShowStatusDialog(r)} className="cursor-pointer">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${sc.cls}`}>
                          <StatusIcon size={12} /> {sc.label}
                        </span>
                      </button>
                      {r.report_type === "aviso" && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                            onClick={() => downloadReport(r.report_id, "xml")} data-testid={`dl-xml-${r.report_id}`}>
                            <FileCode size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                            onClick={() => downloadReport(r.report_id, "txt")} data-testid={`dl-txt-${r.report_id}`}>
                            <FileType size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                            onClick={() => downloadReport(r.report_id, "pdf")} data-testid={`dl-pdf-${r.report_id}`}>
                            <Download size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <NodDialog open={showNodDialog} onClose={() => setShowNodDialog(false)} onGenerated={loadData} />
        {showStatusDialog && (
          <StatusDialog report={showStatusDialog} onClose={() => setShowStatusDialog(null)} onUpdated={loadData} />
        )}
      </div>
    </Layout>
  );
}

function NodDialog({ open, onClose, onGenerated }) {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/aml-reports/nod`, { period }, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `NOD_${period}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte NOD generado");
      onClose();
      onGenerated();
    } catch {
      toast.error("Error generando reporte NOD");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="nod-dialog">
        <DialogHeader>
          <DialogTitle>Generar Reporte NOD</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-400">Genera un reporte batch de todas las operaciones reportables del periodo.</p>
          <div>
            <Label>Periodo (YYYY-MM)</Label>
            <Input className="bg-slate-800 border-slate-700" value={period}
              onChange={(e) => setPeriod(e.target.value)} placeholder="2026-01" data-testid="nod-period-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleGenerate} disabled={generating} data-testid="nod-generate-btn">
            {generating ? "Generando..." : "Generar NOD"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({ report, onClose, onUpdated }) {
  const [status, setStatus] = useState(report.submission_status || "generated");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/aml-reports/aviso/${report.report_id}/status`,
        { status, notes }, { withCredentials: true });
      toast.success(`Estado actualizado a: ${status}`);
      onClose();
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar estado");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="status-dialog">
        <DialogHeader>
          <DialogTitle>Actualizar Estado del Aviso</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-500">Aviso: {report.report_id}</p>
          <div>
            <Label>Estado</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="generated">Generado</SelectItem>
                <SelectItem value="submitted">Enviado al Portal</SelectItem>
                <SelectItem value="accepted">Aceptado por UIF</SelectItem>
                <SelectItem value="rejected">Rechazado</SelectItem>
                <SelectItem value="corrected">Corregido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notas</Label>
            <Input className="bg-slate-800 border-slate-700" value={notes}
              onChange={(e) => setNotes(e.target.value)} placeholder="Notas opcionales" data-testid="status-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="status-save-btn">
            {saving ? "Guardando..." : "Actualizar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
